import { setState, UploadFileAction, setFileList, uploadFile } from '../actions';
import api from '../../../../../../services/api';
import { call, put, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { notification } from 'antd';
import { FileDocumentType, MemoConfig, RelatedEntityType } from '../../types/file';
import { getCompanyLibrary } from '../selectors';
import { CompanyLibraryState } from '../reducers';
import { AudienceNotVisibleError } from '../../types/memo';
import { confirmGrantAccess } from '../../components/Memos/confirmGrantAccess';

/** Appends the memo configuration to the multipart body. */
export const appendMemoFields = (formData: FormData, memo: MemoConfig, grantViewAccess?: boolean) => {
  formData.append('IsMemo', memo.IsMemo ? 'true' : 'false');
  formData.append('AcknowledgementPolicy', memo.AcknowledgementPolicy);
  formData.append('AcknowledgementAudience', JSON.stringify(memo.AcknowledgementAudience || {}));
  formData.append('AlertAudience', JSON.stringify(memo.AlertAudience || {}));
  formData.append('AcknowledgementText', memo.AcknowledgementText || '');
  formData.append('ReminderFrequency', memo.ReminderFrequency);
  if (grantViewAccess) {
    formData.append('GrantViewAccess', 'true');
  }
};

export function* uploadFileWorker(action: UploadFileAction) {
  const {
    file,
    externalUrl,
    fileClientName,
    notes,
    currentFileID,
    relatedEntities,
    expirationDate,
    effectiveDate,
    currentFolderID,
    memo,
    grantViewAccess,
  } = action.payload;
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  if (externalUrl) {
    formData.append('Type', FileDocumentType.Link);
    formData.append('ExternalUrl', externalUrl);
  }
  formData.append('FileClientName', fileClientName);
  formData.append('Notes', notes);
  formData.append('ExpirationDate', expirationDate);
  formData.append('EffectiveDate', effectiveDate);
  formData.append('CurrentFolderId', currentFolderID);
  if (!relatedEntities.length || !relatedEntities[0].Type) {
    formData.append('RelatedEntities', null);
  } else {
    formData.append('RelatedEntities', JSON.stringify(relatedEntities));
  }
  // Links cannot be memos and version uploads never change memo settings.
  if (memo && !externalUrl && !(currentFileID && file)) {
    appendMemoFields(formData, memo, grantViewAccess);
  }
  yield put(setState({ isLoadingList: true, isUploading: true }));

  try {
    let response: AxiosResponse;
    if (currentFileID) {
      if (file) {
        response = yield call(api.patch, `/v1/company-library/${currentFileID}`, formData);
      } else {
        response = yield call(api.patch, `/v1/company-library/details/${currentFileID}`, formData);
      }
    } else {
      response = yield call(api.post, `/v1/company-library`, formData);
    }

    if (response.status === 200) {
      // Get current file list and add the new file
      const { fileList, loadMoreAvailable } = (yield select(
        getCompanyLibrary,
      )) as CompanyLibraryState;

      if (currentFileID) {
        const updatedFile = response.data.Data;
        yield put(setState({ selectedFile: updatedFile }));
        yield put(
          setState({
            fileList: fileList.map(file => (file.ID === updatedFile.ID ? updatedFile : file)),
          }),
        );
      }

      if (!currentFileID) {
        const newFileList = [...fileList, response.data.Data];
        yield put(setFileList(newFileList, loadMoreAvailable));
      }
    }
    yield call(updateRelations);
    return response;
  } catch (error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 422 && data?.Error === 'audience_not_visible') {
      yield put(setState({ isLoadingList: false, isUploading: false }));
      const grant: boolean = yield call(confirmGrantAccess, data as AudienceNotVisibleError);
      if (grant) {
        yield put(uploadFile({ ...action.payload, grantViewAccess: true }));
      }
      return;
    }
    if (status === 400 && typeof data?.message === 'string') {
      notification.error({ message: 'Could not save document', description: data.message });
      return;
    }
    Sentry.captureMessage(`Error uploading file: ${error}`);
    notification.error({
      message: 'Error',
      description: 'There was an error uploading the file. Please try again or contact support.',
    });
  } finally {
    yield put(setState({ isLoadingList: false, isUploading: false }));
  }
}

export function* updateRelations() {
  const relationsResponse = yield call(api.get, `/v1/company-library/relations`);
  if (relationsResponse.status === 200) {
    let updatedRelations = [];
    if (relationsResponse.data.Data !== null) {
      updatedRelations = relationsResponse.data.Data;
    }
    yield put(setState({ allRelations: updatedRelations }));
  }
}
